import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAdminLinkMonitor } from "@/hooks/useAdminLinkMonitor";
import { useAdminMarketplaces } from "@/hooks/useAdminMarketplaces";
import { useAdminProducts, type AdminProduct } from "@/hooks/useAdminStore";
import AdminHeader from "@/pages/admin/components/AdminHeader";
import ProductModal, { hasStoredProductDraft } from "@/pages/admin/components/ProductModal";
import ConfirmDialog from "@/pages/admin/components/ConfirmDialog";
import { getMarketplaceFulfillmentLabel, isAmazonMarketplace } from "@/utils/marketplace";

const MARKETPLACE_COLORS: Record<string, string> = {
  Amazon: "bg-amber-100 text-amber-700",
  "Mercado Livre": "bg-yellow-100 text-yellow-700",
  Shopee: "bg-orange-100 text-orange-700",
  "Magazine Luiza": "bg-blue-100 text-blue-700",
  Americanas: "bg-red-100 text-red-700",
  Kabum: "bg-purple-100 text-purple-700",
};

export default function AdminProducts() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { products, addProduct, updateProduct, deleteProduct } = useAdminProducts();
  const { activeMarketplaceOptions, activeOfficialStoreOptions } = useAdminMarketplaces();
  const { runCheckForItem } = useAdminLinkMonitor();
  const [search, setSearch] = useState("");
  const [filterMarketplace, setFilterMarketplace] = useState("Todos");
  const [filterCategory, setFilterCategory] = useState("Todos");
  const [filterOfficialStore, setFilterOfficialStore] = useState("Todos");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<AdminProduct | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [draftRestoredOnPageLoad, setDraftRestoredOnPageLoad] = useState(false);

  const editId = Number(searchParams.get("edit") || 0);
  const returnTo = searchParams.get("returnTo") || "";
  const isFixFlow = Boolean(returnTo && editId);
  const offersCount = products.filter((product) => product.showInDailyDeals).length;
  const highlightsCount = products.filter((product) => product.showInFeatured).length;

  const marketplaces = useMemo(
    () => ["Todos", ...Array.from(new Set([...activeMarketplaceOptions, ...products.map((p) => p.marketplace)].filter(Boolean)))],
    [activeMarketplaceOptions, products]
  );
  const categories = useMemo(() => ["Todos", ...Array.from(new Set(products.map((p) => p.category)))], [products]);
  const officialStores = useMemo(
    () => ["Todos", ...Array.from(new Set([...activeOfficialStoreOptions, ...products.map((p) => p.officialStoreName).filter(Boolean)]))],
    [activeOfficialStoreOptions, products]
  );

  const filtered = products.filter((p) => {
    const matchSearch = p.name.toLowerCase().includes(search.toLowerCase());
    const matchMp = filterMarketplace === "Todos" || p.marketplace === filterMarketplace;
    const matchCat = filterCategory === "Todos" || p.category === filterCategory;
    const matchStore = filterOfficialStore === "Todos" || p.officialStoreName === filterOfficialStore;
    return matchSearch && matchMp && matchCat && matchStore;
  });

  const clearEditSearchParams = useCallback(() => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("edit");
    nextParams.delete("returnTo");
    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }, []);

  useEffect(() => {
    if (!editId) return;
    const product = products.find((item) => item.id === editId);
    if (!product) return;
    setEditingProduct(product);
    setModalOpen(true);
  }, [editId, products]);

  useEffect(() => {
    if (draftRestoredOnPageLoad || editId || modalOpen) return;
    if (!hasStoredProductDraft()) return;

    setEditingProduct(null);
    setModalOpen(true);
    setDraftRestoredOnPageLoad(true);
    showToast("Rascunho de produto restaurado automaticamente.");
  }, [draftRestoredOnPageLoad, editId, modalOpen, showToast]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
    setEditingProduct(null);
    if (editId) {
      clearEditSearchParams();
    }
  }, [clearEditSearchParams, editId]);

  const handleSave = async (data: Omit<AdminProduct, "id">) => {
    setSaving(true);

    try {
      if (editingProduct) {
        updateProduct(editingProduct.id, data);
        await runCheckForItem("product", editingProduct.id);
        showToast("Produto atualizado com sucesso!");

        if (returnTo) {
          navigate(decodeURIComponent(returnTo));
          return;
        }
      } else {
        addProduct(data);
        showToast("Produto adicionado com sucesso!");
      }

      setModalOpen(false);
      setEditingProduct(null);
      clearEditSearchParams();
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (product: AdminProduct) => {
    setEditingProduct(product);
    setModalOpen(true);
  };

  const handleDelete = () => {
    if (deletingId !== null) {
      deleteProduct(deletingId);
      setDeletingId(null);
      showToast("Produto Apagado com Sucesso!");
    }
  };

  return (
    <div>
      <AdminHeader
        title="Produtos"
        subtitle={`${products.length} produtos cadastrados · ${offersCount} em ofertas · ${highlightsCount} em destaque`}
        action={
          <button
            onClick={() => { setEditingProduct(null); setModalOpen(true); }}
            className="px-4 py-2.5 rounded-xl bg-orange-500 text-white text-sm font-bold hover:bg-orange-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-1.5"></i>Novo Produto
          </button>
        }
      />

      {isFixFlow && (
        <div className="mb-6 rounded-2xl border border-orange-100 bg-orange-50 px-5 py-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-semibold text-orange-700">Fluxo de correção rápida de link quebrado</p>
            <p className="text-sm text-orange-600 mt-0.5">
              Salve o cadastro e você voltará automaticamente para a lista de links quebrados.
            </p>
          </div>
          <button
            onClick={() => navigate(decodeURIComponent(returnTo))}
            className="px-4 py-2 rounded-xl border border-orange-200 bg-white text-sm font-semibold text-orange-600 hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            Voltar ao relatório
          </button>
        </div>
      )}

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative flex-1 min-w-48">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar produto..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100"
          />
        </div>
        <select
          value={filterMarketplace}
          onChange={(e) => setFilterMarketplace(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
        >
          {marketplaces.map((marketplace) => <option key={marketplace}>{marketplace}</option>)}
        </select>
        <select
          value={filterOfficialStore}
          onChange={(e) => setFilterOfficialStore(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
        >
          <option value="Todos">Todas as lojas</option>
          {officialStores.filter((s) => s !== "Todos").map((store) => (
            <option key={store} value={store}>{store}</option>
          ))}
        </select>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-orange-400 cursor-pointer"
        >
          {categories.map((category) => <option key={category}>{category}</option>)}
        </select>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50">
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Produto</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Categoria</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Marketplace</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Preço</th>
              <th className="text-left px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Link</th>
              <th className="text-right px-5 py-3.5 text-xs font-semibold text-gray-500 uppercase tracking-wide">Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center py-12 text-gray-400 text-sm">
                  <i className="ri-shopping-bag-3-line text-3xl block mb-2"></i>
                  Nenhum produto encontrado
                </td>
              </tr>
            )}
            {filtered.map((product) => {
              const fulfillmentLabel = getMarketplaceFulfillmentLabel(product.marketplace);
              const fulfillmentColors = isAmazonMarketplace(product.marketplace)
                ? "text-amber-700 bg-amber-50"
                : "text-sky-700 bg-sky-50";

              return (
                <tr key={product.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-10 h-10 rounded-lg object-cover flex-shrink-0 bg-gray-100"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate max-w-xs">{product.name}</p>
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          {product.badge && (
                            <span className="text-xs text-orange-500 font-medium">{product.badge}</span>
                          )}
                          {product.fulfillment && (
                            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${fulfillmentColors}`}>{fulfillmentLabel}</span>
                          )}
                          {product.freeShipping && (
                            <span className="text-[11px] font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded-full">Frete grátis</span>
                          )}
                          {product.showInDailyDeals && (
                            <span className="text-[11px] font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded-full">Oferta do Dia</span>
                          )}
                          {product.showInFeatured && (
                            <span className="text-[11px] font-semibold text-orange-600 bg-orange-50 px-2 py-0.5 rounded-full">Destaque</span>
                          )}
                        </div>
                        {product.officialStoreName && (
                          <div className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700">
                            <i className="ri-verified-badge-fill"></i>
                            {product.officialStoreName}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className="text-xs text-gray-600 bg-gray-100 px-2.5 py-1 rounded-full font-medium">{product.category}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex flex-col items-start gap-1.5">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${MARKETPLACE_COLORS[product.marketplace] || "bg-gray-100 text-gray-600"}`}>
                        {product.marketplace}
                      </span>
                      {product.officialStoreName && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-blue-700 font-medium">
                          <i className="ri-verified-badge-fill"></i>
                          {product.officialStoreName}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3.5">
                    <p className="text-sm font-bold text-gray-900">R$ {product.price.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p>
                    {product.discount > 0 && (
                      <p className="text-xs text-red-500 font-semibold">-{product.discount}%</p>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    {product.affiliateLink && product.affiliateLink !== "#" ? (
                      <a
                        href={product.affiliateLink}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        className="flex items-center gap-1 text-xs text-orange-500 font-semibold hover:underline cursor-pointer"
                      >
                        <i className="ri-external-link-line"></i>
                        Ver link
                      </a>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Não definido</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(product)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-orange-50 text-gray-400 hover:text-orange-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button
                        onClick={() => setDeletingId(product.id)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                      >
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {modalOpen && (
        <ProductModal
          product={editingProduct}
          onSave={handleSave}
          onClose={closeModal}
          saving={saving}
        />
      )}

      {deletingId !== null && (
        <ConfirmDialog
          title="Apagar Produto"
          message="Deseja realmente Apagar esse Produto"
          confirmLabel="Sim"
          cancelLabel="Não"
          onConfirm={handleDelete}
          onCancel={() => setDeletingId(null)}
        />
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-900 text-white text-sm font-medium px-5 py-3 rounded-xl animate-fade-in z-50">
          <i className="ri-checkbox-circle-line text-green-400 mr-2"></i>
          {toast}
        </div>
      )}
    </div>
  );
}